import type { FrameGeometry } from './bacResultToPredictions';

type FunctionalGroup = {
  PlanePositionSequence?: { ImagePositionPatient?: unknown[] }[];
  PlaneOrientationSequence?: { ImageOrientationPatient?: unknown[] }[];
  PixelMeasuresSequence?: { PixelSpacing?: unknown[] }[];
};

/** The part of a naturalized DICOM instance a frame plane is read from. */
export type FramedInstance = {
  NumberOfFrames?: number | string;
  ImagePositionPatient?: unknown[];
  ImageOrientationPatient?: unknown[];
  PixelSpacing?: unknown[];
  SharedFunctionalGroupsSequence?: FunctionalGroup[];
  PerFrameFunctionalGroupsSequence?: FunctionalGroup[];
};

function toNumbers(values: unknown[] | undefined): number[] | undefined {
  return values?.map(Number);
}

/**
 * The plane of a frame (numbered from 1), from the per-frame functional group,
 * then the shared one, then the top-level attributes: the order OHIF's
 * `combineFrameInstance` uses for the images it displays.
 */
export function getFrameGeometry(
  instance: FramedInstance,
  frameNumber: number
): FrameGeometry | undefined {
  const numberOfFrames = Number(instance.NumberOfFrames) || 1;
  if (frameNumber < 1 || frameNumber > numberOfFrames) {
    return undefined;
  }
  const perFrame = instance.PerFrameFunctionalGroupsSequence?.[frameNumber - 1];
  const shared = instance.SharedFunctionalGroupsSequence?.[0];

  const imagePositionPatient = toNumbers(
    perFrame?.PlanePositionSequence?.[0]?.ImagePositionPatient ??
      shared?.PlanePositionSequence?.[0]?.ImagePositionPatient ??
      instance.ImagePositionPatient
  );
  const imageOrientationPatient = toNumbers(
    perFrame?.PlaneOrientationSequence?.[0]?.ImageOrientationPatient ??
      shared?.PlaneOrientationSequence?.[0]?.ImageOrientationPatient ??
      instance.ImageOrientationPatient
  );
  const pixelSpacing = toNumbers(
    perFrame?.PixelMeasuresSequence?.[0]?.PixelSpacing ??
      shared?.PixelMeasuresSequence?.[0]?.PixelSpacing ??
      instance.PixelSpacing
  );

  if (!imagePositionPatient || !imageOrientationPatient || !pixelSpacing) {
    return undefined;
  }
  return { imagePositionPatient, imageOrientationPatient, pixelSpacing };
}
